// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@/components/link';
import { TruncatedText } from '@/components/truncated-text';
import {
  SuccessStatusIcon,
  WarningStatusIcon,
} from '@/constants/hardfin-standard-icons';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import {
  DepreciationScheduleAsset,
  DepreciationScheduleDisplayMode,
  DepreciationSchedulePeriod,
} from '@/report/api/types';
import { getExceptionConfig } from '@/report/utilities/exception-config';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridPinnedColumnFields,
} from '@mui/x-data-grid-premium';
import { useMemo } from 'react';

type GridColumnDefinition = GridColDef<DepreciationScheduleAsset>;

interface Props {
  assets: DepreciationScheduleAsset[];
  periods: DepreciationSchedulePeriod[];
  displayMode: DepreciationScheduleDisplayMode;
  organizationName: string;
  isLoading?: boolean;
}

const PINNED_COLUMNS: GridPinnedColumnFields = {
  left: ['serial', 'itemName'],
};

function createBaseColumns(organizationName: string): GridColumnDefinition[] {
  return [
    {
      field: 'serial',
      headerName: 'Serial',
      width: 160,
      sortable: true,
      renderCell: (parameters) => (
        <Link
          to="/assets/$assetKey"
          color="primary.main"
          variant="body2"
          params={{ assetKey: parameters.row.key }}
        >
          {parameters.row.serial}
        </Link>
      ),
    },
    {
      field: 'itemName',
      headerName: 'Item',
      width: 220,
      sortable: true,
      valueGetter: (_, row) => row.item.name,
      renderCell: (parameters) => (
        <TruncatedText
          text={`${parameters.row.item.sku} (${parameters.row.item.name})`}
          variant="body2"
        />
      ),
    },
    {
      field: 'locationName',
      headerName: 'Location',
      width: 180,
      sortable: true,
      valueGetter: (_, row) => {
        if (row.currentLocation) {
          return getEffectiveLocationName(row.currentLocation);
        }
        return row.isInTransit ? 'In transit' : '';
      },
      renderCell: (parameters) => {
        if (parameters.row.currentLocation) {
          return (
            <TruncatedText
              text={getEffectiveLocationName(parameters.row.currentLocation)}
              variant="body2"
            />
          );
        }

        return (
          <TruncatedText
            text={parameters.row.isInTransit ? 'In transit' : '—'}
            variant="body2"
            color={parameters.row.isInTransit ? 'text.secondary' : undefined}
          />
        );
      },
    },
    {
      field: 'locationCustomerName',
      headerName: 'Location customer',
      width: 180,
      sortable: true,
      valueGetter: (_, row) =>
        row.currentLocation
          ? (row.currentLocation.customer?.name ?? organizationName)
          : row.isInTransit
            ? 'In transit'
            : '',
      renderCell: (parameters) => {
        if (!parameters.row.currentLocation) {
          return (
            <Typography
              variant="body2"
              color={parameters.row.isInTransit ? 'text.secondary' : undefined}
            >
              {parameters.row.isInTransit ? 'In transit' : '—'}
            </Typography>
          );
        }

        return (
          <TruncatedText
            text={
              parameters.row.currentLocation.customer?.name ?? organizationName
            }
            variant="body2"
            color={
              parameters.row.currentLocation.customer === null
                ? 'text.secondary'
                : undefined
            }
          />
        );
      },
    },
    {
      field: 'inventoryDate',
      headerName: 'Inventory date',
      width: 140,
      sortable: true,
      valueGetter: (_, row) => row.inInventoryDate.toISODate(),
      renderCell: (parameters) => (
        <Typography variant="body2">
          {parameters.row.inInventoryDate.toLocaleString({
            dateStyle: 'medium',
          })}
        </Typography>
      ),
    },
    {
      field: 'inServiceDate',
      headerName: 'In-service date',
      width: 140,
      sortable: true,
      valueGetter: (_, row) =>
        row.accounting.inServiceDate?.toISODate() ?? null,
      renderCell: (parameters) => {
        const value = parameters.row.accounting.inServiceDate;
        if (value === null) {
          return <Typography variant="body2">—</Typography>;
        }

        return (
          <Typography variant="body2">
            {value.toLocaleString({ dateStyle: 'medium' })}
          </Typography>
        );
      },
    },
    {
      field: 'capitalizedCosts',
      headerName: 'Capitalized costs',
      width: 140,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      sortable: true,
      valueGetter: (_, row) =>
        row.accounting.capitalizedCosts === null
          ? null
          : row.accounting.capitalizedCosts.amount.toNumber(),
      renderCell: (parameters) => {
        const value = parameters.row.accounting.capitalizedCosts;
        if (value === null) {
          return <Typography variant="body2">—</Typography>;
        }

        return (
          <Typography variant="body2">
            {formatCurrencyValue(value, { sparse: true })}
          </Typography>
        );
      },
    },
    {
      field: 'usefulLife',
      headerName: 'Useful life',
      width: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      sortable: true,
      valueGetter: (_, row) => row.accounting.usefulLife,
      renderCell: (parameters) => {
        if (parameters.row.accounting.usefulLife === null) {
          return <Typography variant="body2">—</Typography>;
        }

        return (
          <Typography variant="body2">
            {formatNumber(parameters.row.accounting.usefulLife, { digits: 0 })}
          </Typography>
        );
      },
    },
    {
      field: 'exceptions',
      headerName: 'Exceptions',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      valueGetter: (_, row) => {
        const exceptionLabels = row.accounting.assetExceptions.map(
          (exception) => getExceptionConfig(exception).label
        );
        return exceptionLabels.length === 0
          ? 'No exceptions'
          : exceptionLabels.join(', ');
      },
      renderCell: (parameters) => (
        <ExceptionStatusCell
          exceptionLabels={parameters.row.accounting.assetExceptions.map(
            (exception) => getExceptionConfig(exception).label
          )}
        />
      ),
    },
  ];
}

function ExceptionStatusCell(props: { exceptionLabels: string[] }) {
  if (props.exceptionLabels.length === 0) {
    return (
      <Tooltip arrow placement="top" title="No exceptions">
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'success.main',
          }}
        >
          <SuccessStatusIcon fontSize="small" />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Stack spacing={0.5}>
          <Typography fontSize="inherit" fontWeight="bold">
            This asset has exceptions:
          </Typography>
          <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
            {props.exceptionLabels.map((label) => (
              <Typography key={label} component="li" fontSize="inherit">
                {label}
              </Typography>
            ))}
          </Stack>
        </Stack>
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'warning.main',
        }}
      >
        <WarningStatusIcon fontSize="small" />
      </Box>
    </Tooltip>
  );
}

function createPeriodColumnDefinition(
  period: DepreciationSchedulePeriod,
  index: number,
  displayMode: DepreciationScheduleDisplayMode
): GridColumnDefinition {
  return {
    type: 'number',
    field: period.startDate.toISODate() ?? period.label,
    headerName: period.label,
    width: 130,
    align: 'right',
    headerAlign: 'right',
    sortable: false,
    valueGetter: (_, row) => {
      const value = row.cells[index]?.[displayMode];
      return value === null || value === undefined ? null : Number(value);
    },
    renderCell: (parameters) => {
      const value = parameters.row.cells[index]?.[displayMode];
      if (value === null || value === undefined) {
        return <Typography variant="body2">—</Typography>;
      }

      return (
        <Typography variant="body2">
          {formatAmountWithCurrency(
            value,
            parameters.row.accounting.costCurrencyCode,
            { sparse: true }
          )}
        </Typography>
      );
    },
  };
}

export function DepreciationScheduleReportTable(props: Props) {
  const theme = useTheme();

  const columns = useMemo(() => {
    return props.periods.reduce<GridColumnDefinition[]>(
      (acc, period, index) => [
        ...acc,
        createPeriodColumnDefinition(period, index, props.displayMode),
      ],
      createBaseColumns(props.organizationName)
    );
  }, [props.displayMode, props.organizationName, props.periods]);

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        minWidth: 0,
        minHeight: 420,
        maxHeight: 820,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: theme.shadows[6],
      }}
    >
      <DataGridPremium
        hideFooter
        disableColumnMenu
        disableColumnResize
        disableColumnReorder
        disableRowSelectionOnClick
        density="compact"
        rows={props.assets}
        columns={columns}
        loading={props.isLoading}
        initialState={{ pinnedColumns: PINNED_COLUMNS }}
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
        }}
      />
    </Paper>
  );
}
